import {
  CartItemKeys,
  CartKeys,
  ReconstructedCartItemKeys,
  ReconstructedCartKeys,
} from '@config/keys/cart.keys';
import { DoodProductModel } from './product.model';
import { DoodShopModel } from './shop.model';
import { IOnBehalfUser, IValidateDeliveryAddress } from './order.model';
import { ProductOriginWithAddedValue } from './product-origin.model';

export interface DoodCartModel {
  [CartKeys.Shop]?: string;
  [CartKeys.Handle]: string;
  [CartKeys.CartId]?: string;
  [CartKeys.CartCreatedAt]?: Date;
  [CartKeys.OnSiteLocationId]?: string;
  [CartKeys.OnSiteLocationName]?: string;
  [CartKeys.PaymentService]?: string;
  [CartKeys.Message]?: string;
  [CartKeys.User]?: string;
  [CartKeys.Type]: string;
  [CartKeys.AuthMode]?: string;
  [CartKeys.WhiteLabel]: boolean;
  [CartKeys.Marketplace]: string;
  [CartKeys.MultiShop]: boolean;
  [CartKeys.CustomerDevice]: string;
  [CartKeys.AppPlatform]: string;
  [CartKeys.Coupons]?: ICoupon[];
  [CartKeys.WantedAt]: Date | null;
  [CartKeys.Products]: CartItemWithOrigin[];
  [CartKeys.RegisterUserToComo]?: boolean;
  [CartKeys.UsePoints]?: ICartUsePoint;
  [CartKeys.ContactPhone]?: string;
  [CartKeys.ShopSlug]?: string;
  [CartKeys.DeliveryAddress]?: IValidateDeliveryAddress;
  [CartKeys.Tip]?: number;
  [CartKeys.PaymentPhoneNumber]?: string;
  [CartKeys.OnBehalfOf]?: IOnBehalfUser;
  [CartKeys.IsUserSelectedDistributionMode]?: boolean;
  [CartKeys.Instructions]?: string | null;
  // Frontend: Snapshot of `preorderingAllowed` persisted with the cart so it can survive
  // a page reload and be used as a fallback when the shop is not yet hydrated
  [CartKeys.LastPreorderingAllowedKnown]?: boolean;
}
export type ICartDraftItem = Omit<ICartItem, CartItemKeys.Id>;
export type ICartDraftDeclinedProduct = ICartDeclinableAdditionsItem;
export type ICartDraftSimpleProductItem = Omit<ICartSimpleProductItem, CartItemKeys.Id>;
export type ICartDraftSimpleProductOfCategory = ICartSimpleProductOfCategoryItem;
export type ICartDraftDeclinableProductItem = Omit<ICartDeclinableProductItem, CartItemKeys.Id>;
// todo review to differentiate CartSimpleProductItem and CartDeclinableProductItem
export interface ICartItem {
  [CartItemKeys.Id]: number | string;
  [CartItemKeys.ItemId]: string;
  [CartItemKeys.ShopId]: string;
  [CartItemKeys.Products]?: ICartDeclinableAdditionsItem[];
  [CartItemKeys.Additions]: string[];
  [CartItemKeys.Quantity]: number;
  /**
   *  usable only for “Open Price” products (retrieved from plugin like TCPOS)
   * In cents (e.g. 1099 = $10.99)
   */
  [CartItemKeys.Amount]?: number;
}

export const NOT_HANDLED_PRODUCT_ORIGIN = undefined;

export interface CartItemWithOrigin extends ICartItem {
  [CartItemKeys.Origin]: ProductOriginWithAddedValue | typeof NOT_HANDLED_PRODUCT_ORIGIN;
}

export interface ICartDeclinableProductItem {
  [CartItemKeys.Id]: number | string;
  [CartItemKeys.ItemId]: string;
  [CartItemKeys.ShopId]: string;
  /**
   * ! make sure to create an item per quantity (the quantity must be 1)
   */
  [CartItemKeys.Products]?: ICartDraftDeclinedProduct[];
  [CartItemKeys.Quantity]: number;
}

export interface ICartDeclinableAdditionsItem {
  [CartItemKeys.Id]: string;
  [CartItemKeys.ShopId]: string;
  [CartItemKeys.Additions]?: string[];
  [CartItemKeys.Quantity]: number;
  [CartItemKeys.StoreId]?: string;
  [CartItemKeys.SeparateAdditionsByGroup]?: ICartDeclinableSeparateAdditions[];
}

export interface ICartSimpleProductOfCategoryItem {
  [CartItemKeys.Id]: string;
  [CartItemKeys.ShopId]: string;
  [CartItemKeys.Additions]: string[];
  [CartItemKeys.Quantity]: number;
  [CartItemKeys.StoreId]: string;
  [CartItemKeys.SeparateAdditionsByGroup]: ICartDeclinableSeparateAdditions[];
}

export interface ICartSimpleProductItem {
  [CartItemKeys.Id]: string;
  [CartItemKeys.ItemId]: string;
  [CartItemKeys.ShopId]: string;
  [CartItemKeys.Additions]: string[];
  [CartItemKeys.Quantity]: number;
}

export interface ICartDeclinableSeparateAdditions {
  id: string;
  selected: string[];
}

export interface DoodReconstructedCart {
  [ReconstructedCartKeys.Shop]: DoodShopModel;
  [ReconstructedCartKeys.Items]: DoodReconstructedCartItem[];
}

export interface DoodReconstructedCartItem {
  [ReconstructedCartItemKeys.Item]: ICartItem;
  [ReconstructedCartItemKeys.Product]: DoodProductModel;
  [ReconstructedCartItemKeys.ChildCompoundItems]?: DoodProductModel[];
  [ReconstructedCartItemKeys.UnitPrice]?: number;
}

export type OrderCoupon = ICoupon | PrepaymentCoupon | SubsidyCoupon;

export interface ICoupon {
  code?: string;
  key?: string;
  amount_off?: number;
  balance?: number;
  deal_code?: any;
  error?: string;
  item_code?: string;
  name?: string;
  percent_off?: number;
  type?: string;
  valid?: boolean;
  removeInProgress?: boolean;
  is_removable?: boolean;
}

export type PrepaymentCoupon = ICoupon & {
  type: CouponTypes['Prepayment'];
  amount_off: number;
  is_removable: false;
};

export type SubsidyCoupon = ICoupon & {
  type: CouponTypes['Subsidy'];
  amount_off: number;
  is_removable: false;
};

export const CouponTypes = {
  Prepayment: 'PREPAYMENT',
  Subsidy: 'SUBSIDY',
} as const;
export type CouponTypes = typeof CouponTypes;
export type CouponType = (typeof CouponTypes)[keyof typeof CouponTypes];

export const NO_PREPAYMENT_COUPON = {
  'type': 'NO_PREPAYMENT' as const,
} satisfies ICoupon;

export interface ICartUsePoint {
  [CartKeys.UsePointsAmount]: number;
  [CartKeys.UsePointsVerificationCode]?: string;
}

export interface DoodCartComment {
  type: string;
  message?: string;
}
